import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  AiSeoOverviewSchema,
  AiSeoRunResponseSchema,
  AiSeoSnapshotListResponseSchema,
  GscConnectResponseSchema,
  GscStatusSchema,
  KeywordListItemSchema,
  KeywordListResponseSchema,
  SeoOverviewSchema,
  type AiSeoOverview,
  type AiSeoSnapshot,
  type CreateKeywordInput,
  type GscStatus,
  type KeywordListItem,
  type SeoOverview,
} from "@/api/types/marketing";

/** Query-key factory — mirrors useDashboard's/useReviews' convention. */
export const marketingKeys = {
  all: ["marketing"] as const,
  aiSeoOverview: () => [...marketingKeys.all, "ai-seo", "overview"] as const,
  aiSeoSnapshots: (limit: number) => [...marketingKeys.all, "ai-seo", "snapshots", limit] as const,
  seoOverview: () => [...marketingKeys.all, "seo", "overview"] as const,
  keywords: () => [...marketingKeys.all, "seo", "keywords"] as const,
  gscStatus: () => [...marketingKeys.all, "seo", "gsc-status"] as const,
};

/** GET /v2/marketing/ai-seo/overview — the headline AI-visibility read. */
export function useAiSeoOverview() {
  return useQuery<AiSeoOverview>({
    queryKey: marketingKeys.aiSeoOverview(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/marketing/ai-seo/overview");
      return validateResponse(AiSeoOverviewSchema, data, "GET /v2/marketing/ai-seo/overview");
    },
    staleTime: 30 * 1000,
  });
}

/**
 * POST /v2/marketing/ai-seo/run — triggers a live AI-visibility check
 * against every enabled provider. Slow (hits real AI APIs), so callers
 * should show a "this can take a minute" note while `isPending`.
 */
export function useRunAiSeoCheck() {
  const queryClient = useQueryClient();
  return useMutation<AiSeoSnapshot, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.post("/v2/marketing/ai-seo/run");
      const body = validateResponse(AiSeoRunResponseSchema, data, "POST /v2/marketing/ai-seo/run");
      return body.snapshot;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketingKeys.aiSeoOverview() });
      void queryClient.invalidateQueries({ queryKey: marketingKeys.all });
    },
  });
}

/** GET /v2/marketing/ai-seo/snapshots — history list, not used by the headline view but exposed for later drill-in. */
export function useAiSeoSnapshots(limit = 12) {
  return useQuery<AiSeoSnapshot[]>({
    queryKey: marketingKeys.aiSeoSnapshots(limit),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/marketing/ai-seo/snapshots?limit=${limit}`);
      return validateResponse(
        AiSeoSnapshotListResponseSchema,
        data,
        "GET /v2/marketing/ai-seo/snapshots",
      ).items;
    },
    staleTime: 60 * 1000,
  });
}

/** GET /v2/marketing/seo/overview — Google rankings, connected-state + keyword rows. */
export function useSeoOverview() {
  return useQuery<SeoOverview>({
    queryKey: marketingKeys.seoOverview(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/marketing/seo/overview");
      return validateResponse(SeoOverviewSchema, data, "GET /v2/marketing/seo/overview");
    },
    staleTime: 30 * 1000,
  });
}

/** GET /v2/marketing/seo/keywords — the tracked-keyword list (distinct from seo/overview's positioned rows). */
export function useSeoKeywords() {
  return useQuery<KeywordListItem[]>({
    queryKey: marketingKeys.keywords(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/marketing/seo/keywords");
      return validateResponse(KeywordListResponseSchema, data, "GET /v2/marketing/seo/keywords").items;
    },
    staleTime: 30 * 1000,
  });
}

/** POST /v2/marketing/seo/keywords — add a keyword to track. */
export function useCreateSeoKeyword() {
  const queryClient = useQueryClient();
  return useMutation<KeywordListItem, unknown, CreateKeywordInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/marketing/seo/keywords", input);
      return validateResponse(KeywordListItemSchema, data, "POST /v2/marketing/seo/keywords");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: marketingKeys.keywords() });
      void queryClient.invalidateQueries({ queryKey: marketingKeys.seoOverview() });
    },
  });
}

/** GET /v2/marketing/seo/gsc/status — whether Search Console is already connected. */
export function useGscStatus() {
  return useQuery<GscStatus>({
    queryKey: marketingKeys.gscStatus(),
    queryFn: async () => {
      const { data } = await apiClient.get("/v2/marketing/seo/gsc/status");
      return validateResponse(GscStatusSchema, data, "GET /v2/marketing/seo/gsc/status");
    },
    staleTime: 30 * 1000,
  });
}

/**
 * GET /v2/marketing/seo/gsc/connect — fetches the OAuth `auth_url` to open.
 * Modeled as a mutation (not a query) since it's fired by a button click,
 * not rendered data — the GET verb here is the contract's, this hook just
 * wraps it as an imperative action.
 */
export function useConnectGsc() {
  return useMutation<string, unknown, void>({
    mutationFn: async () => {
      const { data } = await apiClient.get("/v2/marketing/seo/gsc/connect");
      return validateResponse(GscConnectResponseSchema, data, "GET /v2/marketing/seo/gsc/connect")
        .auth_url;
    },
  });
}
